import React, {useState} from 'react'
import {toggle} from 'opticks'

import {Box} from '@daedalus/atlas/helpers/Box'
import {Text} from '@daedalus/atlas/Text'
import {useBrand} from '@daedalus/core/src/_web/brand/hooks/useBrand'
import {
  BreakdownType,
  Price as BookingPrice
} from '@daedalus/core/src/api-types/bovio/response/booking'
import {FluffyType as TaxesType} from '@daedalus/core/src/api-types/bovio/response/offer_check'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {OfferPrice} from '@daedalus/core/src/offer/types/offer'
import {CurrencyDisplayVariant} from '@daedalus/core/src/price/business/formatPrice'
import {
  getDisplayPrice,
  getHotelFeeByType,
  getPriceDetails
} from '@daedalus/core/src/price/business/price'

import {getTotalChargeLaterDifferentialPrice} from '../OfferDetails/utils'
import {TaxesAndFees} from '../TaxesAndFees'
import {FeeTypes} from '.'
import PriceLine from './PriceLine'
import {
  getTaxesAndFeesPopupBodyText,
  getTaxesAndFeesPopupHeaderText
} from './TaxesAndFeesMessages'
import {TranslatedOtherFees} from './TranslatedOtherFees'
interface PriceBreakdownProps {
  displayTransparentFees: boolean
  showFeesDescriptions: boolean
  onFeesDetailsClick?: () => void
  isPriceLoading?: boolean
  currencyDisplayVariant?: CurrencyDisplayVariant
  /** Prices */
  prices: OfferPrice[] | BookingPrice[]
  /** Whether the price is pay at property */
  isPayAtProperty: boolean
  isChargeLaterSelected?: boolean
  propertyCurrencyCode: string | undefined
  showTaxes?: boolean
  numberOfRooms: number
  numberOfNights: number
  isSplitBooking?: boolean
}

const PriceBreakdown = ({
  displayTransparentFees = false,
  showFeesDescriptions,
  isPriceLoading = false,
  onFeesDetailsClick,
  currencyDisplayVariant,
  prices,
  isPayAtProperty,
  isChargeLaterSelected,
  propertyCurrencyCode,
  numberOfRooms,
  numberOfNights,
  isSplitBooking = false
}: PriceBreakdownProps) => {
  const [taxesAndFeesModalOpen, setTaxesAndFeesModalOpen] = useState(false)
  const {brandName} = useBrand()

  // Using this value only for offer-check and booking-retrieve as it has the detailed hotel fees breakdown
  const displayPrice = getDisplayPrice<OfferPrice | BookingPrice>(prices)

  const {
    displayCurrencyCode,
    taxesDisplayPrice,
    chargeableCurrencyCode,
    hotelFeesTotal,
    chargeableBase
  } = getPriceDetails(prices, isPayAtProperty)

  const baseRate = toggle(
    '50feb427-cl-differential-price',
    chargeableBase,
    isChargeLaterSelected
      ? getTotalChargeLaterDifferentialPrice(chargeableBase)
      : chargeableBase
  )

  const taxes = toggle(
    '50feb427-cl-differential-price',
    Number(taxesDisplayPrice),
    isChargeLaterSelected
      ? getTotalChargeLaterDifferentialPrice(Number(taxesDisplayPrice))
      : Number(taxesDisplayPrice)
  )
  const currencyCode = String(displayCurrencyCode)
  const totalTaxes = taxes + hotelFeesTotal
  const isVat =
    displayPrice &&
    'taxes' in displayPrice &&
    displayPrice.taxes &&
    displayPrice.taxes.breakdown.length === 1 &&
    displayPrice.taxes.breakdown[0].type === TaxesType.Vat
  const handleTaxesAndFeesClose = () => {
    setTaxesAndFeesModalOpen(false)
  }

  const onInfoPopupOpen = () => {
    onFeesDetailsClick?.()
    setTaxesAndFeesModalOpen(isOpen => !isOpen)
  }
  return (
    <>
      {!displayTransparentFees ? (
        taxes && taxes > 0 ? (
          <>
            <PriceLine
              label={getTaxesAndFeesPopupHeaderText(FeeTypes.TaxesFees)}
              isPriceLoading={isPriceLoading}
              showFeesDescriptions={showFeesDescriptions}
              price={totalTaxes}
              currencyCode={currencyCode}
              onInfoClick={onInfoPopupOpen}
              currencyDisplayVariant={currencyDisplayVariant}
            />
            <TaxesAndFees
              currencyCode={currencyCode}
              chargeableCurrencyCode={chargeableCurrencyCode}
              propertyCurrencyCode={propertyCurrencyCode}
              hotelFeesTotal={hotelFeesTotal}
              taxes={taxes}
              isOpen={taxesAndFeesModalOpen}
              onClose={handleTaxesAndFeesClose}
            />
          </>
        ) : null
      ) : (
        <Box>
          <Box paddingBottom="s400">
            <Text variant="titleM">
              <FormattedMessageWrapper
                id="priceDetails"
                defaultMessage="Price details"
              />
            </Text>
          </Box>
          <Box
            gap="s250"
            paddingBottom="s400"
            display="flex"
            flexDirection="column"
          >
            {/* Following 2 rows, Base rate and tax rows are taken from chargeable prices */}
            <PriceLine
              noMargin
              label={
                <FormattedMessageWrapper
                  id="priceDetails.roomsAndNights"
                  defaultMessage="{rooms, plural, =0 {} one {1 room x } other {# rooms x }}{nights, plural, one {1 night} other {# nights}}"
                  values={{
                    rooms: isSplitBooking ? 0 : numberOfRooms,
                    nights: numberOfNights
                  }}
                />
              }
              price={baseRate}
              currencyCode={currencyCode}
            />
            {taxes && taxes > 0 ? (
              <PriceLine
                label={getTaxesAndFeesPopupHeaderText(
                  isVat ? FeeTypes.Vat : FeeTypes.Tax
                )}
                tooltipBody={
                  !isVat
                    ? getTaxesAndFeesPopupBodyText(FeeTypes.Tax, brandName)
                    : null
                }
                isPriceLoading={isPriceLoading}
                showFeesDescriptions={!isVat}
                price={taxes}
                currencyCode={currencyCode}
                currencyDisplayVariant={currencyDisplayVariant}
                noMargin
              />
            ) : null}
            {/* Following rows are taken from hotelFees and are pay at property */}
            {displayPrice?.hotelFees?.breakdown
              ?.filter(fee => fee.total)
              ?.map(fee => (
                <PriceLine
                  key={fee.type}
                  isPayAtProperty
                  label={getTaxesAndFeesPopupHeaderText(fee.type)}
                  price={getHotelFeeByType(fee.type, displayPrice)}
                  currencyCode={currencyCode}
                  currencyDisplayVariant={currencyDisplayVariant}
                  noMargin
                  {...(fee.type === BreakdownType.Other &&
                    'elements' in fee &&
                    fee.elements?.length && {
                      tooltipBody: (
                        <TranslatedOtherFees elements={fee.elements} />
                      ),
                      showFeesDescriptions: true
                    })}
                />
              ))}
          </Box>
        </Box>
      )}
    </>
  )
}

export default PriceBreakdown
